/**
 * Hook pobiera świeży podpisany WMS URL dla projektu QGIS i auto-refreshuje
 * go przed wygaśnięciem (TTL po stronie backendu = 1h).
 *
 * PO CO TO ISTNIEJE:
 *   Po wariancie B (ALB direct routing /qgis/* → nginx-cache, bypass Django)
 *   publiczny endpoint /qgis/ był dostępny BEZ żadnej autentykacji. Każdy
 *   z UUID projektu (uciekającym z DevTools u zalogowanego usera) mogłby
 *   curlem pobrać tile'e mapy publicznie.
 *
 *   Signed URL z HMAC + 1h expiration eliminuje to: nginx waliduje signature
 *   (secure_link directive) zanim forwarduje do qgis_server. Bez ważnego
 *   podpisu = 403. Patrz:
 *     - backend/apps/gis/signing.py — algorytm HMAC + endpoint
 *     - nginx/qgis-cache.conf.template — `secure_link` walidacja
 *     - terraform/prod/secrets.tf — `random_password.qgis_sign_secret`
 *
 * AUTO-REFRESH:
 *   TTL signed URL = 1h. Hook re-fetch'uje co 50 min (10 min marginesu na
 *   zegar drift / opóźnienia sieciowe). React Query staleTime = 50 min,
 *   refetchInterval = 50 min (też w tle przy schowanej karcie).
 *
 *   Stare Leaflet tile w map cache pozostają (Leaflet trzyma je w pamięci
 *   przez sesję, niezależnie od URL). Tylko NOWE requests (pan/zoom)
 *   używają nowego signed URL.
 *
 * CACHE HIT W NGINX:
 *   nginx ma `proxy_cache_key` ustawiony NA PARAMS WMS (map + LAYERS + BBOX
 *   + WIDTH + HEIGHT + FORMAT + CRS + STYLES + TRANSPARENT), bez md5/expires.
 *   Czyli mimo że co 50 min zmienia się signed URL, klucz cache jest taki
 *   sam → hit rate utrzymany.
 */
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/client";

interface SignedWmsUrlResponse {
  wms_url: string;
  expires_in_seconds: number;
  /** Zasięg podglądu DXF [minx, miny, maxx, maxy] w EPSG:3857 (null dla
   *  projektów zbudowanych przed wprowadzeniem pola). */
  extent_3857: [number, number, number, number] | null;
  /** Statyczne kafle XYZ (gdal2tiles → S3): baza dla {z}/{x}/{y}.png. Gdy niepuste,
   *  mapa serwuje je zamiast WMS (szybko, bez QGIS). null = brak, użyj WMS. */
  raster_tiles_url?: string | null;
  /** Natywny max zoom kafli — powyżej Leaflet skaluje (blur) zamiast 404. */
  raster_tiles_max_zoom?: number | null;
}

const TTL_MS = 60 * 60 * 1000;        // backend mówi że 1h
const REFRESH_MARGIN_MS = 10 * 60 * 1000; // 10 min marginesu na drift / lag
const REFRESH_INTERVAL_MS = TTL_MS - REFRESH_MARGIN_MS;  // 50 min

export function useSignedWmsUrl(projectId: string | undefined) {
  return useQuery<SignedWmsUrlResponse>({
    queryKey: ["gis-signed-wms-url", projectId],
    queryFn: () =>
      apiClient
        .get<{ data: SignedWmsUrlResponse }>(
          `/gis/projects/${projectId}/wms-url/`,
        )
        .then((r) => r.data.data),
    enabled: !!projectId,
    staleTime: REFRESH_INTERVAL_MS,
    gcTime: TTL_MS,
    refetchInterval: REFRESH_INTERVAL_MS,
    // Continue refreshing nawet gdy user przeszedł na inną kartę — żeby
    // nie wracając do mapy nie zobaczył 403 z wygasłego URL.
    refetchIntervalInBackground: true,
  });
}
